import React, { useEffect, useMemo, useState } from "react";
import FirstNavbar from "../dashboard/FirstNavbar";
import SideBar from "../dashboard/SideBar";
import { Link } from "react-router-dom";
import axios from "axios";
import baseURL from "../../Services/Url";
import { Modal, Button, Row, Form, Col, Spinner } from "react-bootstrap";
import { ToastContainer, toast, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AgGridReact } from "ag-grid-react";
import Select from "react-select";
import {
  blocksFromBlog,
  cleanContentBlocks,
  legacyFieldsFromBlocks,
} from "./BlogContentBlocks";
import BlogContentBlocks from "./BlogContentBlocks";

const slugify = (value) =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const splitCsv = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const getImageUrl = (item) => item.featuredImage || item.image || "";

const normalizeListResponse = (data) => {
  if (Array.isArray(data)) {
    return data.flat().filter(Boolean);
  }

  if (Array.isArray(data?.items)) {
    return data.items.flat().filter(Boolean);
  }

  return [];
};

function BlogList() {
  const [reloadPage, setReloadPage] = useState(1);
  const [loader, setLoader] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [updateID, setUpdateId] = useState(null);
  const [show, setShow] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);
  const [updateBlogForm, setUpdateBlogForm] = useState({
    title: "",
    contentBlocks: blocksFromBlog(),
    report_date: "",
    blog_type: "",
    author: "",
    featuredImage: null,
    currentImage: "",
    altText: "",
    slug: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    tags: [],
  });

  useEffect(() => {
    productsListApi();
    fetchTags();
  }, [reloadPage]);

  const selectedTagOptions = useMemo(
    () =>
      tagOptions.filter((option) =>
        updateBlogForm.tags.includes(option.value)
      ),
    [tagOptions, updateBlogForm.tags]
  );

  const productsListApi = () => {
    axios
      .get(`${baseURL}/blogs`)
      .then((res) => {
        if (res.status === 200) {
          setRowData(normalizeListResponse(res.data));
        }
      })
      .catch((err) => {
        console.log(err, "err");
        toast.error("Unable to load blogs");
      });
  };

  const fetchTags = () => {
    axios
      .get(`${baseURL}/tags`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setTagOptions(
            res.data.map((tag) => ({
              value: tag.slug || tag.name || tag.title,
              label: tag.name || tag.title || tag.slug,
            }))
          );
        }
      })
      .catch(() => {
        toast.error("Unable to load blog tags");
      });
  };

  const handleDelete = (ele) => {
    setShowDelete(true);
    setDeleteId(ele);
  };

  const handleDeleteSubmit = () => {
    if (!deleteId?.id) return;
    setLoader(true);
    axios
      .delete(`${baseURL}/blogs/${deleteId.id}`)
      .then((res) => {
        if (res.status === 200) {
          toast.success("Successfully Deleted");
          setReloadPage((prev) => prev + 1);
          setShowDelete(false);
        }
      })
      .catch((err) => {
        console.log(err, "delete error");
        toast.error(err.response?.data?.message || "Unable to delete blog");
      })
      .finally(() => setLoader(false));
  };

  const handleUpdate = (ele) => {
    setShow(true);
    setUpdateBlogForm({
      title: ele.title || "",
      contentBlocks: blocksFromBlog(ele),
      report_date: ele.report_date || "",
      blog_type: ele.blog_type || "",
      author: ele.author || "",
      featuredImage: null,
      currentImage: getImageUrl(ele),
      altText: ele.altText || "",
      slug: ele.slug || slugify(ele.title || ""),
      metaTitle: ele.metaTitle || ele.title || "",
      metaDescription: ele.metaDescription || "",
      metaKeywords: Array.isArray(ele.metaKeywords)
        ? ele.metaKeywords.join(", ")
        : ele.metaKeywords || "",
      tags: Array.isArray(ele.tags) ? ele.tags : [],
    });
    setUpdateId(ele);
  };

  const updateField = (name, value) => {
    setUpdateBlogForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "title" && !prev.slug ? { slug: slugify(value) } : {}),
      ...(name === "title" && !prev.metaTitle ? { metaTitle: value } : {}),
    }));
  };

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    updateField(name, name === "featuredImage" ? files[0] : value);
  };

  const handleTagsChange = (options) => {
    updateField(
      "tags",
      (options || []).map((option) => option.value)
    );
  };

  const handleClear = () => {
    setUpdateBlogForm({
      title: "",
      contentBlocks: blocksFromBlog(),
      report_date: "",
      blog_type: "",
      author: "",
      featuredImage: null,
      currentImage: "",
      altText: "",
      slug: "",
      metaTitle: "",
      metaDescription: "",
      metaKeywords: "",
      tags: [],
    });
  };

  const handleSubmit = () => {
    if (!updateID?.id) return;
    const contentBlocks = cleanContentBlocks(updateBlogForm.contentBlocks);
    if (!contentBlocks.some((block) => block.type === "text")) {
      toast.error("Please add at least one text content block");
      return;
    }
    const legacy = legacyFieldsFromBlocks(contentBlocks);
    setLoader(true);
    const formData = new FormData();
    formData.append("category", "blog");
    formData.append("title", updateBlogForm.title);
    formData.append("description", legacy.content);
    formData.append("content", legacy.content);
    formData.append("contentBlocks", JSON.stringify(contentBlocks));
    formData.append("report_date", updateBlogForm.report_date);
    formData.append("blog_type", updateBlogForm.blog_type);
    formData.append("author", updateBlogForm.author);
    formData.append("slug", slugify(updateBlogForm.slug));
    formData.append("metaTitle", updateBlogForm.metaTitle);
    formData.append("metaDescription", updateBlogForm.metaDescription);
    formData.append("metaKeywords", JSON.stringify(splitCsv(updateBlogForm.metaKeywords)));
    formData.append("altText", updateBlogForm.altText);
    formData.append("tags", JSON.stringify(updateBlogForm.tags));
    formData.append("charts", JSON.stringify(legacy.charts));
    formData.append("graphs", JSON.stringify([]));
    formData.append("tables", JSON.stringify(legacy.tables));

    if (updateBlogForm.featuredImage) {
      formData.append("featuredImage", updateBlogForm.featuredImage);
    }

    axios({
      url: `${baseURL}/blogs/${updateID.id}`,
      method: "PUT",
      data: formData,
    })
      .then((res) => {
        if (res.status === 200) {
          setReloadPage((prev) => prev + 1);
          setShow(false);
          toast.success("Successfully Updated Blog");
          handleClear();
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Unable to update blog");
      })
      .finally(() => setLoader(false));
  };

  const rowHeight = 56;
  const DefaultColumnSetting = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    flex: 1,
    resizable: true,
    minWidth: 120,
  };

  const colDefs = [
    {
      headerName: "Id",
      valueGetter: "node.rowIndex + 1",
      filter: true,
      lockPosition: true,
      maxWidth: 90,
    },
    {
      headerName: "Title",
      filter: true,
      field: "title",
    },
    {
      headerName: "Slug",
      filter: true,
      field: "slug",
    },
    {
      headerName: "Meta Title",
      filter: true,
      field: "metaTitle",
    },
    {
      headerName: "Report Date",
      filter: true,
      field: "report_date",
    },
    {
      headerName: "Blog Type",
      filter: true,
      field: "blog_type",
    },
    {
      headerName: "Author",
      filter: true,
      field: "author",
    },
    {
      headerName: "Image",
      filter: false,
      cellRendererFramework: (params) => (
        <center>
          {getImageUrl(params.data) && (
            <img
              style={{ width: "50px", height: "50px", objectFit: "cover" }}
              src={getImageUrl(params.data)}
              alt={params.data.altText || params.data.title || "Blog"}
            />
          )}
        </center>
      ),
    },
    {
      headerName: "Update",
      filter: false,
      maxWidth: 120,
      cellRendererFramework: (params) => (
        <center onClick={() => handleUpdate(params.data)}>
          <button type="button" className="btn btn-outline-primary">
            Edit
          </button>
        </center>
      ),
    },
    {
      headerName: "Delete",
      filter: false,
      maxWidth: 120,
      cellRendererFramework: (params) => (
        <center onClick={() => handleDelete(params.data)}>
          <button type="button" className="btn btn-outline-danger">
            Delete
          </button>
        </center>
      ),
    },
  ];

  return (
    <div>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar
        transition={Zoom}
        delay={500}
        limit={1}
      />
      <Modal show={showDelete} onHide={() => setShowDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Blog</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this blog?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteSubmit} disabled={loader}>
            {loader ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={show} onHide={() => setShow(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Update Blog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Title"
                  name="title"
                  value={updateBlogForm.title}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>URL Slug</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="blog-url-slug"
                  name="slug"
                  value={updateBlogForm.slug}
                  onChange={(event) =>
                    updateField("slug", slugify(event.target.value))
                  }
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Report Date</Form.Label>
                <Form.Control
                  type="date"
                  name="report_date"
                  value={updateBlogForm.report_date}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Blog Type</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Blog Type"
                  name="blog_type"
                  value={updateBlogForm.blog_type}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Author</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Author"
                  name="author"
                  value={updateBlogForm.author}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Featured Image</Form.Label>
                <Form.Control
                  type="file"
                  name="featuredImage"
                  onChange={handleChange}
                  autoComplete="off"
                />
                {updateBlogForm.currentImage && (
                  <img
                    src={updateBlogForm.currentImage}
                    alt={updateBlogForm.altText || "Current blog"}
                    className="mt-2"
                    style={{ width: 90, height: 70, objectFit: "cover" }}
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Featured Image Alt Tag</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Describe the image"
                  name="altText"
                  value={updateBlogForm.altText}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="border-top pt-3 mt-2">
            <h5>SEO</h5>
            <Form.Group className="mb-3">
              <Form.Label>Title Tag</Form.Label>
              <Form.Control
                type="text"
                name="metaTitle"
                value={updateBlogForm.metaTitle}
                onChange={handleChange}
                autoComplete="off"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Meta Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="metaDescription"
                value={updateBlogForm.metaDescription}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Meta Keywords</Form.Label>
              <Form.Control
                type="text"
                name="metaKeywords"
                value={updateBlogForm.metaKeywords}
                onChange={handleChange}
                placeholder="keyword one, keyword two"
                autoComplete="off"
              />
            </Form.Group>
          </div>

          <div className="border-top pt-3 mt-2">
            <h5>Blog Tags</h5>
            <Form.Group className="mb-3">
              <Form.Label>Assign Tags</Form.Label>
              <Select
                isMulti
                options={tagOptions}
                value={selectedTagOptions}
                onChange={handleTagsChange}
                placeholder="Select blog tags"
              />
            </Form.Group>
          </div>

          <BlogContentBlocks
            blocks={updateBlogForm.contentBlocks}
            onChange={(contentBlocks) => updateField("contentBlocks", contentBlocks)}
            onError={(err) =>
              toast.error(
                err.response?.data?.message || err.message || "Unable to upload content image"
              )
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClear}>
            Clear
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loader}>
            {loader ? <Spinner animation="border" size="sm" /> : "Update"}
          </Button>
        </Modal.Footer>
      </Modal>

      <div>
        <div className="sidebar">
          <SideBar />
        </div>

        <div className="content">
          <div className="container">
            <FirstNavbar />

            <div className="row my-2">
              <div className="col-md-3">
                <h3>
                  <b>Blogs List</b>
                </h3>
              </div>
              <div className="col-md-4 text-end">
                <button type="button" className="btn btn-outline-success mx-1">
                  <Link to="/blog-add">Add Blog</Link>
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success mx-1"
                  disabled
                >
                  <Link to="/blog-list">Blogs List</Link>
                </button>
              </div>
            </div>

            <div
              className="ag-theme-alpine"
              style={{ height: "75vh", width: "100%" }}
            >
              <AgGridReact
                rowHeight={rowHeight}
                columnDefs={colDefs}
                defaultColDef={DefaultColumnSetting}
                pagination={true}
                paginationPageSize={10}
                rowData={rowData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlogList;
